import Commons from '../../../../Services/Common/Common';
import dayjs from 'dayjs';

/**
 * 산업재해현황(ACS_BASIC_01) 스키마
 * 컬럼: 순번, 발생일시, 부서, 직급, 성명, 입사일, 전화번호, 발생장소, 공정(기인물), 재해발생형태, 상해종류,
 *       사고경위, 조치, 원인, 개선대책, 휴업(예상)일수, 복귀(예정)일자, 산업재해보상보험 신청여부,
 *       산업재해조사표, 사고경위서, 비고
 */
export const ACS_BASIC_01_Schema = {
  fields: {
    accidentSeq: { initial: null, label: '순번' },
    accidentDate: { initial: '', label: '발생일시', required: 'Y' },
    depNm: { initial: '', label: '부서', required: 'Y' },
    rank: { initial: '', label: '직급' },
    victimName: { initial: '', label: '성명', required: 'Y' },
    joinDate: { initial: '', label: '입사일' },
    tel: { initial: '', label: '전화번호' },
    location: { initial: '', label: '발생장소' },
    gongjongCd: { initial: '', label: '공정코드' },
    gongjongNm: { initial: '', label: '공정(기인물)', required: 'Y' },
    accidentType: { initial: '', label: '재해발생형태' },
    injuryType: { initial: '', label: '상해종류' },
    accidentDesc: { initial: '', label: '사고경위', required: 'Y' },
    actionTaken: { initial: '', label: '조치' },
    causeDesc: { initial: '', label: '원인' },
    preventMeasure: { initial: '', label: '개선대책' },
    leaveDays: { initial: '', label: '휴업(예상)일수' },
    returnDate: { initial: '', label: '복귀(예정)일자' },
    compInsuranceStat: { initial: '', label: '산업재해보상보험 신청여부' },
    investigationStatFileName: { initial: '', label: '산업재해조사표' },
    reportFileName: { initial: '', label: '사고경위서' },
    remark: { initial: '', label: '비고' },
  },
  mapper: {
    search1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      StartDate: (_, context) =>
        Commons.EncryptAES(context.search?.startDate || '') || '',
      EndDate: (_, context) =>
        Commons.EncryptAES(context.search?.endDate || '') || '',
      VictimName: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
    },
    save1: {
      AccidentSeq: (row) => {
        const value = row.accidentSeq;
        return (value === '' || value === undefined) ? null : Number(value);
      },
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      AccidentDate: (row) => {
        const v = row.accidentDate;
        if (!v) {
          return Commons.EncryptAES('') || '';
        }
        const formatted = dayjs(v).isValid()
          ? dayjs(v).format('YYYY-MM-DD HH:mm:ss')
          : '';
        return Commons.EncryptAES(formatted) || '';
      },
      VictimId: (row) => Commons.EncryptAES(row.victimId || '') || '',
      JoinDate: (row) => {
        const v = row.joinDate;
        if (!v) return Commons.EncryptAES('') || '';
        const formatted = dayjs(v).isValid()
          ? dayjs(v).format('YYYY-MM-DD')
          : '';
        return Commons.EncryptAES(formatted) || '';
      },
      GongjongCd: (row) => Commons.EncryptAES(row.gongjongCd || '') || '',
      Location: (row) => Commons.EncryptAES(row.location || '') || '',
      AccidentType: (row) => Commons.EncryptAES(row.accidentType || '') || '',
      InjuryType: (row) => Commons.EncryptAES(row.injuryType || '') || '',
      AccidentDesc: (row) => Commons.EncryptAES(row.accidentDesc || '') || '',
      ActionTaken: (row) => Commons.EncryptAES(row.actionTaken || '') || '',
      CauseDesc: (row) => Commons.EncryptAES(row.causeDesc || '') || '',
      PreventMeasure: (row) => Commons.EncryptAES(row.preventMeasure || '') || '',
      LeaveDays: (row) => {
        const v = row.leaveDays;
        if (v == null || v === '') return 0;
        const n = parseInt(v, 10);
        return isNaN(n) ? 0 : n;
      },
      ReturnDate: (row) => {
        const v = row.returnDate;
        if (!v) return Commons.EncryptAES('') || '';
        const formatted = dayjs(v).isValid()
          ? dayjs(v).format('YYYY-MM-DD HH:mm:ss')
          : '';
        return Commons.EncryptAES(formatted) || '';
      },
      CompInsuranceStat: (row) => Commons.EncryptAES(row.compInsuranceStat || '') || '',
      InvFileName: (row) => Commons.EncryptAES(row.investigationStatFileName || '') || '',
      InvFilePath: (row) => Commons.EncryptAES(row.investigationStatFilePath || '') || '',
      InvSaveName: (row) => Commons.EncryptAES(row.investigationStatSaveName || '') || '',
      ReportFileName: (row) => Commons.EncryptAES(row.reportFileName || '') || '',
      ReportFilePath: (row) => Commons.EncryptAES(row.reportFilePath || '') || '',
      ReportSaveName: (row) => Commons.EncryptAES(row.reportSaveName || '') || '',
      Remark: (row) => Commons.EncryptAES(row.remark || '') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    /** DeleteAccBasic01D1: CustCd(암호화), AccidentSeq(산업재해 일련번호) */
    delete1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      AccidentSeq: (row) => {
        const v = row.accidentSeq ?? row.AccidentSeq;
        if (v == null || v === '') return 0;
        const n = Number(v);
        return Number.isInteger(n) ? n : parseInt(v, 10) || 0;
      },
    },
  },
};

/**
 * GetAccBasic01R1 조회 응답 행을 그리드 행으로 매핑
 * - DB 컬럼: INVESTIGATION_STAT_FILE_NAME 등
 * - 조회 응답: invFileName, invFilePath, invSaveName (camelCase)
 * - 저장 요청: 서버가 InvFileName, InvFilePath, InvSaveName 수신
 */
export const mapGetAccBasic01R1RowToGrid = (row) => {
  if (!row) return null;

  let gongjongCd = row.gongjongCd ?? row.GongjongCd ?? '';
  let gongjongNm = row.gongjongNm ?? row.GongjongNm ?? '';
  const cdTrim = String(gongjongCd ?? '').trim();
  const nmTrim = String(gongjongNm ?? '').trim();
  if (
    cdTrim === '0' ||
    cdTrim === '00' ||
    nmTrim === '기타' ||
    cdTrim === '기타'
  ) {
    gongjongCd = '0';
    gongjongNm = '기타';
  }

  return {
    ...row,
    accidentSeq: row.accidentSeq ?? row.AccidentSeq ?? null,
    accidentDate: row.accidentDate ?? row.AccidentDate ?? '',
    depNm: row.depNm ?? row.DepNm ?? '',
    rank: row.rank ?? row.Rank ?? '',
    victimName: row.victimName ?? row.VictimName ?? '',
    victimId: row.victimId ?? row.VictimId ?? '',
    joinDate: row.joinDate ?? row.JoinDate ?? '',
    tel: row.tel ?? row.Tel ?? '',
    location: row.location ?? row.Location ?? '',
    gongjongCd,
    gongjongNm,
    accidentType: row.accidentType ?? row.AccidentType ?? '',
    injuryType: row.injuryType ?? row.InjuryType ?? '',
    accidentDesc: row.accidentDesc ?? row.AccidentDesc ?? '',
    actionTaken: row.actionTaken ?? row.ActionTaken ?? '',
    causeDesc: row.causeDesc ?? row.CauseDesc ?? '',
    preventMeasure: row.preventMeasure ?? row.PreventMeasure ?? '',
    leaveDays: row.leaveDays ?? row.LeaveDays ?? '',
    returnDate: row.returnDate ?? row.ReturnDate ?? '',
    compInsuranceStat: row.compInsuranceStat ?? row.CompInsuranceStat ?? '',
    remark: row.remark ?? row.Remark ?? '',
    // 산업재해조사표: 조회 inv* -> 그리드 investigationStat*
    investigationStatFileName: row.investigationStatFileName ?? row.invFileName ?? '',
    investigationStatFilePath: row.investigationStatFilePath ?? row.invFilePath ?? '',
    investigationStatSaveName: row.investigationStatSaveName  ?? row.invSaveName ?? '',
    // 사고경위서
    reportFileName: row.reportFileName ?? row.ReportFileName ?? '',
    reportFilePath: row.reportFilePath ?? row.ReportFilePath ?? '',
    reportSaveName: row.reportSaveName ?? row.ReportSaveName ?? '',
  };
};
